// Pure logic for the per-asset Explanation panel (I360A-2744). Backend values
// win: points and totals are only ever checked here, never recomputed.
import {
	BreakdownAxis,
	BreakdownComponent,
	RiskBreakdown,
} from '@Types/riskBreakdown.types';
import { Category } from '@Types/category.types';
import { TFunc } from '@Translations/types';
import { coverageBandKey } from '@Components/RiskDetails/Datagrid/RenderCell/coverageBand';

export const TOLERANCE = 0.01;

export const toNumber = (v: unknown): number | null => {
	if (v === null || v === undefined || v === '') return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
};

export interface Coverage {
	real: number;
	total: number;
	pct: number;
}

// Real ÷ all configured components. noData rows have usedDefault true, so they
// count as missing; "no feature in range" has usedDefault false and counts as real.
// Deselected rows (I360A-3651) are not configured inputs, so they count on neither
// side. Twin of BE computeDataCoverage (dataCoverage.ts): change both together.
export const computeCoverage = (b: RiskBreakdown): Coverage | undefined => {
	const comps = [b.LOF, b.CoF]
		.flatMap(a => a.categories.flatMap(c => c.components))
		.filter(c => c.defaultReason !== 'deselected');
	if (comps.length === 0) return undefined;
	const real = comps.filter(c => !c.usedDefault).length;
	return {
		real,
		total: comps.length,
		pct: Math.round((real / comps.length) * 100),
	};
};

export type Qualifier = 'dontAct' | 'mayUnderstate' | null;

// Minimal coverage qualifies every grade: Concerning/Critical (3, 4) get
// "don't act yet", the rest "may understate".
export const selectQualifier = (pct: number, grade: number): Qualifier => {
	if (coverageBandKey(pct) !== 'minimal') return null;
	return grade >= 3 ? 'dontAct' : 'mayUnderstate';
};

export const round = (v: number, dp: number): number => {
	const f = Math.pow(10, dp);
	return Math.round(v * f) / f;
};

// Largest-remainder rounding: each value to `dp` places so that the rounded
// values sum exactly to the rounded target (default: the rounded sum).
export const apportionRound = (
	values: number[],
	dp: number,
	target?: number,
): number[] => {
	if (values.length === 0) return [];
	const f = Math.pow(10, dp);
	// Clean float noise (0.29 * 100 = 28.999…) before flooring.
	const scaled = values.map(v => Math.round(v * f * 1e6) / 1e6);
	const floors = scaled.map(v => Math.floor(v));
	const wanted = Math.round(
		(target ?? values.reduce((a, b) => a + b, 0)) * f,
	);
	const remaining = wanted - floors.reduce((a, b) => a + b, 0);
	const order = scaled
		.map((v, i) => ({ i, r: v - floors[i] }))
		.sort((a, b) => b.r - a.r || a.i - b.i);
	const out = floors.slice();
	// Units go to the largest remainders first (or come off the smallest when
	// the target is below the floors), cycling if one pass isn't enough, so the
	// result always sums exactly to the target.
	const step = remaining > 0 ? 1 : -1;
	const ranked = step > 0 ? order : order.slice().reverse();
	for (let k = 0; k < Math.abs(remaining); k += 1) {
		out[ranked[k % ranked.length].i] += step;
	}
	return out.map(v => v / f);
};

// Percentages and weights for display: at most 2 dp, trailing zeros trimmed
// (16.666… → "16.67", 14.999… → "15").
export const formatPct = (v: number): string => String(round(v, 2));

/** Gap below which configured and applied percentages count as equal. */
export const PCT_TOLERANCE = 0.005;

export interface ReconciledAxis {
	/** Axis total from the payload, unrounded. */
	total: number | null;
	/** Total at 2 dp; equals the sum of displayPoints when pointsMatchTotal. */
	displayTotal: number | null;
	/** [categoryIndex][componentIndex] points at 2 dp. */
	displayPoints: number[][];
	/** total × axisWeighting / 100: should equal the row's LoF/CoF. */
	weightedTotal: number | null;
	rowValue: number | null;
	matchesRow: boolean;
	pointsMatchTotal: boolean;
}

export const reconcileAxis = (
	axis: BreakdownAxis,
	rowValue: unknown,
): ReconciledAxis => {
	const flat = axis.categories.reduce<number[]>(
		(all, c) => all.concat(c.components.map(x => x.weightedPoints)),
		[],
	);
	const total = toNumber(axis.total);
	const sum = flat.reduce((a, b) => a + b, 0);
	const pointsMatchTotal =
		total === null
			? flat.every(p => p === 0)
			: Math.abs(sum - total) <= TOLERANCE;
	// Inconsistent payload: round to the points' own sum rather than bend
	// them toward a total they don't add up to (the footer flags it).
	const rounded = apportionRound(
		flat,
		2,
		pointsMatchTotal && total !== null ? total : undefined,
	);
	let k = 0;
	const displayPoints = axis.categories.map(c =>
		c.components.map(() => rounded[k++]),
	);
	const weightedTotal =
		total === null ? null : (total * axis.axisWeighting) / 100;
	const row = toNumber(rowValue);
	const matchesRow =
		weightedTotal === null || row === null
			? weightedTotal === row
			: Math.abs(weightedTotal - row) <= TOLERANCE;
	return {
		total,
		displayTotal: total === null ? null : round(total, 2),
		displayPoints,
		weightedTotal,
		rowValue: row,
		matchesRow,
		pointsMatchTotal,
	};
};

export type RiskMethod = 'added' | 'multiplied';
export type RiskNote =
	| 'lofMissing'
	| 'cofMissing'
	| 'lofZeroAsOne'
	| 'cofZeroAsOne';

export interface ReconciledRisk {
	method: RiskMethod;
	lof: number | null;
	cof: number | null;
	/** The engine's combination of the operands. */
	computed: number | null;
	rowRisk: number | null;
	outcome: 'matches' | 'rescaled' | 'mismatch' | 'noRisk';
	notes: RiskNote[];
}

// Mirrors CalculateRISK (saas_risk_management RiskService.ts): multiplied
// uses `parseFloat(v) || 1`, so a missing OR zero side counts as 1; added
// uses `|| 0`. Operands are the row's LoF/CoF (already axis-weighted).
export const reconcileRisk = (a: {
	lof: unknown;
	cof: unknown;
	method: RiskMethod;
	rowRisk: unknown;
	normalised: boolean;
}): ReconciledRisk => {
	const lof = toNumber(a.lof);
	const cof = toNumber(a.cof);
	const rowRisk = toNumber(a.rowRisk);
	const notes: RiskNote[] = [];
	if (lof === null) notes.push('lofMissing');
	else if (lof === 0 && a.method === 'multiplied') notes.push('lofZeroAsOne');
	if (cof === null) notes.push('cofMissing');
	else if (cof === 0 && a.method === 'multiplied') notes.push('cofZeroAsOne');

	if (lof === null && cof === null) {
		return {
			method: a.method,
			lof,
			cof,
			computed: null,
			rowRisk,
			outcome: 'noRisk',
			notes,
		};
	}
	const side = (v: number | null) =>
		a.method === 'multiplied' ? v || 1 : v ?? 0;
	const computed =
		a.method === 'multiplied'
			? side(lof) * side(cof)
			: side(lof) + side(cof);

	let outcome: ReconciledRisk['outcome'];
	if (rowRisk === null) outcome = 'mismatch';
	else if (
		// Relative for large products: the operands are 3-dp row figures, the
		// engine multiplied unrounded values.
		Math.abs(computed - rowRisk) <=
		Math.max(TOLERANCE, Math.abs(rowRisk) * 1e-3)
	)
		outcome = 'matches';
	else outcome = a.normalised ? 'rescaled' : 'mismatch';

	return { method: a.method, lof, cof, computed, rowRisk, outcome, notes };
};

export type ParsedBand =
	| { kind: 'lte'; max: number }
	| { kind: 'between'; min: number; max: number }
	| { kind: 'gt'; min: number };

const NUM = String.raw`(-?\d+(?:\.\d+)?)`;
const BETWEEN = new RegExp(String.raw`^${NUM}\s*<=\s*${NUM}$`);
const LTE = new RegExp(String.raw`^<=\s*${NUM}$`);
const GT = new RegExp(String.raw`^>\s*${NUM}$`);

// Score Setup generates range labels as ` <= a`, `a <= b` and `> b`, unitless,
// in the stored unit. Anything else (template-authored text) isn't parsed.
export const parseBand = (label: string): ParsedBand | null => {
	const s = label.trim();
	let m = BETWEEN.exec(s);
	if (m) return { kind: 'between', min: Number(m[1]), max: Number(m[2]) };
	m = LTE.exec(s);
	if (m) return { kind: 'lte', max: Number(m[1]) };
	m = GT.exec(s);
	if (m) return { kind: 'gt', min: Number(m[1]) };
	return null;
};

export type FormatWithUnit = (value: number, unit: string) => string;

// Friendly precision for display: |n| >= 1000 → 0 dp, >= 100 → 1 dp,
// otherwise up to 2 dp; trailing zeros trimmed, integers unchanged.
const displayDp = (a: number): number => {
	if (a >= 1000) return 0;
	if (a >= 100) return 1;
	return 2;
};

export const roundForDisplay = (n: number): string => {
	if (Number.isInteger(n)) return String(n);
	const a = Math.abs(n);
	return String((n < 0 ? -1 : 1) * round(a, displayDp(a)));
};

// "1234567.5" → "1,234,567.5". A plain loop rather than a lookahead regex,
// which can backtrack badly on long digit runs (Sonar S5852).
const groupThousands = (s: string): string => {
	const [int, frac] = s.split('.');
	const sign = int.startsWith('-') ? '-' : '';
	const digits = sign ? int.slice(1) : int;
	let grouped = '';
	for (let i = digits.length; i > 0; i -= 3) {
		const chunk = digits.slice(Math.max(0, i - 3), i);
		grouped = grouped ? `${chunk},${grouped}` : chunk;
	}
	grouped = sign + grouped;
	return frac === undefined ? grouped : `${grouped}.${frac}`;
};

// A number in display text ("1,234.567 ft", "0 – 351.6762", "1.5-2.5"):
// starts the string or follows whitespace, '(', a comparison or a dash; '-' is
// a sign only at the start or after whitespace/'('/a comparison, otherwise
// it's a range dash kept as the prefix. Optional `,` grouping, `.` decimals,
// and not followed by more number characters (so "0,87" or "1.234,5" from a
// decimal-comma locale, and digits inside units like "ft3", are left alone).
const NUM_TOKEN =
	/(^|[\s(<=>–-])(-?)(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?(?![\d.,])/g;

// Rounds every decimal number in a string, keeping units and other text.
// Integers and anything that doesn't parse cleanly are left as they are.
export const roundFormatted = (s: string): string =>
	s.replaceAll(
		NUM_TOKEN,
		(whole, pre: string, sign: string, int: string, frac?: string) => {
			if (!frac) return whole;
			const rounded = roundForDisplay(
				Number(`${sign}${int.replaceAll(',', '')}${frac}`),
			);
			return `${pre}${
				int.includes(',') ? groupThousands(rounded) : rounded
			}`;
		},
	);

/** `fmt`, with the result rounded for display. */
export const roundedFmt =
	(fmt: FormatWithUnit): FormatWithUnit =>
	(value, unit) =>
		roundFormatted(fmt(value, unit));

// Never invents a unit: an unparseable label is shown as it is; a generated
// label without a unit keeps its wording with the bounds rounded.
export const formatBand = (
	label: string,
	unit: string | null,
	rawFmt: FormatWithUnit,
): string => {
	const p = parseBand(label);
	if (!p) return label.trim();
	if (!unit) return roundFormatted(label.trim());
	const fmt = roundedFmt(rawFmt);
	if (p.kind === 'between')
		return `${fmt(p.min, unit)} – ${fmt(p.max, unit)}`;
	if (p.kind === 'lte') return `≤ ${fmt(p.max, unit)}`;
	return `> ${fmt(p.min, unit)}`;
};

// The search limit for "no feature within X", from the outermost band.
export const noFeatureLimit = (band: string | null): number | null => {
	const p = band ? parseBand(band) : null;
	if (!p) return null;
	return p.kind === 'gt' ? p.min : p.max;
};

// Dates (4-digit years), grades and categorical codes are never rounded.
export const formatComponentValue = (
	c: BreakdownComponent,
	fmt: FormatWithUnit,
): string | null => {
	if (c.value === null) return null;
	const n = toNumber(c.value);
	if (
		n === null ||
		(c.format !== 'number' &&
			c.format !== 'distance' &&
			c.format !== 'count')
	)
		return String(c.value);
	if (c.unit && c.format !== 'count') return roundedFmt(fmt)(n, c.unit);
	return roundForDisplay(n);
};

export const fmtScore = (v: number | null): string => {
	if (v === null) return '—';
	return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

// Why a component was scored from a default; shown as the ⚠ title in the
// axis table and in the Data coverage list.
export const defaultNote = (c: BreakdownComponent, t: TFunc): string | null => {
	const score = fmtScore(c.score);
	switch (c.defaultReason) {
		case 'nullScore':
			return t('No data — scored from default ({{score}})', { score });
		case 'unmatched':
			return t(
				// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
				"'{{value}}' isn't in the score table — scored from default ({{score}})",
				{
					value: String(c.value ?? ''),
					score,
				},
			);
		case 'noData':
			return t('No data — not scored');
		case 'deselected':
			return t('Not used in this model');
		default:
			return null;
	}
};

export interface ScoresOnlyCategory {
	name: string;
	components: { name: string; score: number | null }[];
}

// Fallback for runs with no breakdown: raw 0–10 scores only, looked up by
// exact `${prefix}Raw_${category}_${component}` key (built, never parsed).
// No weights or points: LOF_ entries include the axis factor and the live
// config may differ from the run.
export const buildScoresOnly = (
	cats: Category[] | undefined,
	ws: Record<string, unknown> | undefined,
	prefix: 'LOF' | 'COF',
): ScoresOnlyCategory[] =>
	(cats ?? [])
		.filter(c => c.components?.some(x => x.name))
		.map(c => ({
			name: c.name,
			components: c.components
				.filter(x => x.name)
				.map(x => ({
					name: x.name,
					score: toNumber(ws?.[`${prefix}Raw_${c.name}_${x.name}`]),
				})),
		}));
